var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Object3D } from "three";
import { AddAnnotationIntoCurrentRoomRequestMessage, AddAnnotationIntoCurrentRoomResponseMessage, AskOkMessage, BroadcastToPeerMessage, ChangeInputDeviceButtonMappingMessage, convertToNumber3, convertToNumber4, DeleteModelMessage, DisableGestureMessage, EnableControllerAnnotationMessage, FacetFactory, FacetVisibility, hashCode, InputDeviceType, NetworkLocalOnly, PlaySoundMessage, RemoveFacetFromModelMessage, UpdateFacetOnModelMessage, ArcFacet, MeshFacet, ScriptComposeFacet, ScriptFacet, ControlAnimationMessage } from "flavr-common";
import { Quaternion, Vector3, Matrix4 } from "flavr-math";
import { SetSelectionStrategyForHandsMessage } from "@messages/selection/SelectionStrategyMessages";
import { DropModelMessage, PickupModelMessage, PickupModelResponseMessage, RequestCanPickupModelMessage } from "@messages/attachment/AttachmentMessage";
import { ScriptRPCMessage, ScriptRPCOnlyHostMessage } from "@messages/scripting/ScriptMessages";
import { UI3DRenderable } from "@modules/ui3d/UI3DRenderable";
import { networkEntitiesService } from "@modules/network/NetworkEntitiesService";
import { modelPool, determineModelNameForPrinting, getModelRelativeTo, computeLocalMatrixFromWorldTransformComponents } from "@modules/model";
import { createModelInstance } from "./Utils";
import { ScriptModel } from "./ScriptModel";
import { Property } from "./Property";
import { dropFileService } from "../services/DropFileService";
import { scriptBusService } from "../services/ScriptBusService";
import { SCRIPT_FUNCTION_NAMES_SET } from "../script/ScriptManager";
import { coreUserService } from "../../users";
export class ModelScriptFunctions {
    constructor(context, modelUID, model, room, scriptId, scriptIdx) {
        this.context = context;
        this.modelUID = modelUID;
        this.room = room;
        this.scriptId = scriptId;
        this.scriptIdx = scriptIdx;
        /// UI3D ///
        this.getUiElement = (selector) => {
            var _a;
            const root = UI3DRenderable.elementRegistry.get(this.modelUID);
            return (_a = root === null || root === void 0 ? void 0 : root.querySelector(selector)) !== null && _a !== void 0 ? _a : undefined;
        };
        this.persistentVariable = (variableName) => {
            var _a;
            if (typeof variableName !== "string") {
                throw new Error("persistentVariable: variableName must be of type string");
            }
            const modelScript = this.context.scripts.get(this.scriptIdx);
            if ((modelScript === null || modelScript === void 0 ? void 0 : modelScript.scriptInstance) === undefined)
                return false;
            if (!Object.prototype.hasOwnProperty.call(modelScript.scriptInstance, variableName)) {
                console.error(`[scripting] Error by persistentVariable: variable ${variableName} not defined in class ${(_a = modelScript.scriptName) !== null && _a !== void 0 ? _a : "undefined script"}.
         If you want the variable to be undefined when initializing, please set "${variableName} = undefined;"`);
                return false;
            }
            modelScript.setPersistentVariable(variableName);
            return true;
        };
        /// General ///
        this.getRelativeId = () => {
            return modelPool.getModelOrThrow(this.modelUID).getName();
        };
        this.getName = () => {
            return modelPool.getModelOrThrow(this.modelUID).getName();
        };
        this.destroyMe = (force = false) => {
            const msg = new DeleteModelMessage(this.modelUID);
            const script = this.context.scripts.get(this.scriptIdx);
            if (script !== undefined) {
                // ToDo: add to scriptsToDestroy list?
                script.destroy();
            }
            if (force) {
                this.context.connection.send(msg);
            }
            else {
                this.context.pendingMessages.push(msg);
            }
            return true;
        };
        /// Find script ///
        this.getScriptByClassName = (className) => {
            const model = this.room.models.get(this.modelUID);
            if (model === undefined) {
                return undefined;
            }
            for (const script of model.scripts.values()) {
                const instance = script.scriptInstance;
                if (instance === undefined)
                    continue;
                if (script.scriptName === className) {
                    return this.context.wrapWeakScript(script);
                }
            }
            return undefined;
        };
        this.findScriptsByClassInChildren = (className) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return [];
            const scriptModels = [];
            for (const child of model.getChildren()) {
                const modelData = this.room.models.get(child.getUID());
                if (modelData === undefined)
                    continue;
                for (const script of modelData.scripts.values()) {
                    const scriptName = script.scriptName;
                    if (scriptName === undefined)
                        continue;
                    if (scriptName !== className)
                        continue;
                    const weakScript = this.context.wrapWeakScript(script);
                    if (weakScript === undefined)
                        continue;
                    scriptModels.push(weakScript); // ToDo: does this conversion make sense?
                }
            }
            return scriptModels;
        };
        /// Emit function ///
        this.emit = (funcName, ...params) => {
            if (this.context.currentRoom === undefined)
                return false;
            const model = this.room.models.get(this.modelUID);
            if (model === undefined)
                return false;
            let hasListeners = false;
            for (const script of model.scripts.values()) {
                const instance = script.scriptInstance;
                if (instance === undefined)
                    continue;
                const func = instance[funcName];
                if (typeof func !== "function")
                    continue;
                func.call(instance, ...params);
                hasListeners = true;
            }
            return hasListeners;
        };
        this.emitParent = (funcName, ...params) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return false;
            const parent = model.getParent();
            if (parent === undefined)
                return false;
            const parentModel = this.room.models.get(parent.getUID());
            if (parentModel === undefined)
                return false;
            let hasListeners = false;
            for (const script of parentModel.scripts.values()) {
                const instance = script.scriptInstance;
                if (instance === undefined)
                    continue;
                const func = instance[funcName];
                if (typeof func !== "function")
                    continue;
                func.call(instance, ...params);
                hasListeners = true;
            }
            return hasListeners;
        };
        this.emitAll = (funcName, ...params) => {
            if (this.context.currentRoom === undefined)
                return false;
            let hasListeners = false;
            for (const model of this.context.currentRoom.models.values()) {
                for (const script of model.scripts.values()) {
                    const instance = script.scriptInstance;
                    if (instance === undefined)
                        continue;
                    const func = instance[funcName];
                    if (typeof func !== "function")
                        continue;
                    func.call(instance, ...params);
                    hasListeners = true;
                }
            }
            return hasListeners;
        };
        this.emitTo = (uid, funcName, ...params) => {
            if (uid === undefined) {
                return;
            }
            if (typeof uid !== "number") {
                if (uid instanceof ScriptModel && uid.modelUID !== undefined) {
                    uid = uid.modelUID;
                }
                else if (uid instanceof Property && uid.getCurrentType() === "UID") {
                    uid = uid.getValue();
                }
                else if (uid.uid !== undefined) { // Fix for Model ID
                    console.warn(`[deprecated] emitTo most likely uses Model ID, consider emitting UID instead`, uid);
                    uid = uid.uid;
                }
                else {
                    throw new Error(`uid type of ${typeof uid} is not allowed`);
                }
            }
            const model = this.context.getModelInCurrentRoom(uid);
            if (model === undefined) {
                throw new Error(`emitTo: can't find model for uid ${uid}`);
            }
            let returns;
            for (const script of model.scripts.values()) {
                const instance = script.scriptInstance;
                if (instance === undefined)
                    continue;
                const func = instance[funcName];
                if (typeof func !== "function")
                    continue;
                const ret = func.call(instance, ...params, this.modelUID);
                if (ret !== undefined) {
                    if (returns === undefined) {
                        returns = [];
                    }
                    returns.push(ret);
                }
            }
            if ((returns === null || returns === void 0 ? void 0 : returns.length) === 1) {
                return returns[0];
            }
            return undefined;
        };
        // =========================================================================
        // Common model methods
        // =========================================================================
        /// Find model ///
        this.getModel = () => {
            return createModelInstance(this.modelUID);
        };
        this.getModelByUid = (uid) => {
            if (typeof uid !== "number")
                return undefined;
            const thisRoom = this.getParentRoom(this.modelUID);
            const otherRoom = this.getParentRoom(uid);
            const currentRoom = modelPool.getCurrentRoom();
            if (otherRoom === undefined || thisRoom === undefined || currentRoom === undefined)
                return undefined;
            if (!(thisRoom.equals(otherRoom) || !currentRoom.isAncestorOf(otherRoom)))
                return undefined;
            return createModelInstance(uid);
        };
        this.getParentModel = () => {
            const model = modelPool.getModel(this.modelUID);
            const parent = model === null || model === void 0 ? void 0 : model.getParent();
            if (parent === undefined)
                return undefined;
            return createModelInstance(parent.getUID());
        };
        this.getChildModelByRelativeId = (relativeId) => {
            // ToDo: validate relativeId
            console.warn(`Called getChildModelByRelativeId with relative id "${relativeId}", this function will be removed in the future!`);
            const model = modelPool.getModelOrThrow(this.modelUID);
            const child = getModelRelativeTo(model, relativeId);
            if (child === undefined)
                return undefined;
            return createModelInstance(child.getUID());
        };
        this.findModelsByName = (nameOrPart, options) => {
            return modelPool.findModelsWithName(nameOrPart, options)
                .map(model => createModelInstance(model.getUID()));
        };
        this.findModelsByClass = (className) => {
            const models = [];
            for (const model of this.room.models.values()) {
                for (const script of model.scripts.values()) {
                    const scriptName = script.scriptName;
                    if (scriptName === undefined)
                        continue;
                    if (scriptName !== className)
                        continue;
                    models.push(createModelInstance(script.modelUID));
                }
            }
            return models;
        };
        this.findModelsByFacetType = (facetType) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return [];
            const room = model.getRoom();
            if (room === undefined)
                return [];
            // Iterate and over all descendents in this room that are not rooms themselves
            const currentModelStack = new Array();
            currentModelStack.push(room);
            const models = [];
            while (currentModelStack.length > 0) {
                const currentModel = currentModelStack.pop();
                if (currentModel === undefined)
                    continue;
                if (!currentModel.isRoom() && currentModel.hasFacet(facetType)) {
                    models.push(createModelInstance(currentModel.getUID()));
                }
                // Add all non-room children of current model to the top of the stack
                for (const child of currentModel.getChildren()) {
                    if (!child.isRoom())
                        currentModelStack.push(child);
                }
            }
            return models;
        };
        /// Facet ///
        this.updateFacet = (facet, force = false) => {
            if (!FacetFactory.isValidObject(facet)) {
                return false;
            }
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot update facet ${facet.type} because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return false;
            }
            if (!force) {
                const oldFacet = model.getFacet(facet);
                if (oldFacet !== undefined && oldFacet !== facet && oldFacet.equals(facet))
                    return false;
            }
            model.setFacet(facet, this.broadcastProps);
            return true;
        };
        this.removeFacet = (facetType) => {
            this.context.pendingMessages.push(new RemoveFacetFromModelMessage(this.modelUID, facetType));
            return true;
        };
        this.getFacet = (facetType) => {
            const model = modelPool.getModel(this.modelUID);
            return model === null || model === void 0 ? void 0 : model.getFacet(facetType);
        };
        this.hasFacet = (facetType) => {
            var _a;
            const model = modelPool.getModel(this.modelUID);
            return (_a = model === null || model === void 0 ? void 0 : model.hasFacet(facetType)) !== null && _a !== void 0 ? _a : false;
        };
        this.setFacetVisibility = (type, visible) => {
            this.context.pendingMessages.push(new UpdateFacetOnModelMessage(this.modelUID, new FacetVisibility(type, visible)));
        };
        /// Material ///
        this.setMaterialColor = (materialColor, materialName) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return;
            const arcFacet = model.getFacet(ArcFacet);
            if (arcFacet !== undefined) {
                arcFacet.color = materialColor;
                model.setFacet(arcFacet);
            }
            const meshFacet = model.getFacet(MeshFacet);
            if (meshFacet !== undefined && materialName !== undefined) {
                meshFacet.params.colors = { materialName: materialColor };
                model.setFacet(meshFacet);
            }
            model.flushMessages();
        };
        this.setMaterialParameter = (key, value, expectedType, materialIndex) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return false;
            return model.setMaterialParameter(key, value, expectedType, materialIndex);
        };
        this.getMaterialParameter = (key, expectedType, materialIndex) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return undefined;
            return model.getMaterialParameter(key, expectedType, materialIndex);
        };
        /// Transform model ///
        this.setPosition = (pos) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            model.setPosition(pos, this.broadcastProps);
        };
        this.setOrientation = (orientation) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            model.setOrientation(orientation, this.broadcastProps);
        };
        this.setScale = (scale) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            model.setScale(scale, this.broadcastProps);
        };
        this.setTransform = (position, orientation, scale) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            model.setTransformComponents(position, orientation, scale, this.broadcastProps);
        };
        this.setWorldPosition = (position) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, position, undefined, undefined);
            model.setTransform(localMatrix, this.broadcastProps);
        };
        this.setWorldOrientation = (orientation) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, undefined, orientation, undefined);
            model.setTransform(localMatrix, this.broadcastProps);
        };
        this.setWorldScale = (scale) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, undefined, undefined, scale);
            model.setTransform(localMatrix, this.broadcastProps);
        };
        this.setWorldTransform = (position, orientation, scale) => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined) {
                console.error(`[scripting] Cannot set position because the model ${determineModelNameForPrinting(this.modelUID)} doesn't exist`);
                return;
            }
            const localMatrix = computeLocalMatrixFromWorldTransformComponents(model, position, orientation, scale);
            model.setTransform(localMatrix, this.broadcastProps);
        };
        this.getPosition = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Vector3();
            return model.getPosition();
        };
        this.getOrientation = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Quaternion();
            return model.getOrientation();
        };
        this.getScale = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Vector3(1, 1, 1);
            return model.getScale();
        };
        this.getMatrix = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Matrix4();
            return model.getTransform();
        };
        this.getWorldPosition = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Vector3();
            return model.getWorldPosition();
        };
        this.getWorldOrientation = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Quaternion();
            return model.getWorldOrientation();
        };
        this.getWorldScale = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Vector3(1, 1, 1);
            return model.getWorldScale();
        };
        this.getWorldMatrix = () => {
            const model = modelPool.getModel(this.modelUID);
            if (model === undefined)
                return new Matrix4();
            return model.getWorldTransform();
        };
        this.controlAnimation = (command, clip) => {
            this.context.pendingMessages.push(new ControlAnimationMessage(this.modelUID, command, clip));
        };
        /// User Input ///
        this.enableGesture = (type, enable) => {
            this.context.pendingMessages.push(new DisableGestureMessage(type, !enable));
        };
        this.enableControllerAnnotation = (enable) => {
            this.context.pendingMessages.push(new EnableControllerAnnotationMessage(enable));
        };
        this.setAllButtonsToOneButtonType = (buttonType) => {
            this.context.pendingMessages.push(...[
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.HtcViveController, "0", buttonType),
                // new ChangeInputDeviceButtonMappingMessage(InputDeviceType.HtcViveController, "1", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.HtcViveController, "2", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.HtcViveController, "3", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.ValveIndexController, "0", buttonType),
                // new ChangeInputDeviceButtonMappingMessage(InputDeviceType.ViveIndexController, "1", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.ValveIndexController, "2", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.ValveIndexController, "3", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.ValveIndexController, "4", buttonType),
                // new ChangeInputDeviceButtonMappingMessage(InputDeviceType.OculusController, "1", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.OculusController, "2", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.OculusController, "3", buttonType),
                new ChangeInputDeviceButtonMappingMessage(InputDeviceType.OculusController, "4", buttonType)
            ]);
        };
        this.setControllerStrategyForHands = (hands, strategy) => {
            this.context.pendingMessages.push(new SetSelectionStrategyForHandsMessage(hands, strategy));
        };
        /// Audio ///
        this.playSound = (url, params) => {
            this.context.pendingMessages.push(new PlaySoundMessage(this.modelUID, url, params));
            return true;
        };
        this.broadcastSound = (url, params) => {
            const msg = new PlaySoundMessage(this.modelUID, url, params);
            this.context.pendingMessages.push(msg);
            this.context.pendingMessages.push(new BroadcastToPeerMessage([msg]));
            return true;
        };
        /// Network ///
        this.localOnly = () => {
            this.context.connection.send(new NetworkLocalOnly(this.modelUID));
        };
        this.setNetworked = () => {
            return networkEntitiesService.createNetworkEntity(this.modelUID);
        };
        this.takeOwnership = (destroyWhenOwnerDisconnect) => __awaiter(this, void 0, void 0, function* () {
            return yield networkEntitiesService.takeOwnership(this.modelUID, destroyWhenOwnerDisconnect);
        });
        this.surrenderOwnership = () => {
            return networkEntitiesService.surrenderOwnership(this.modelUID);
        };
        this.isHost = () => {
            return this.context.isRoomHost;
        };
        this.isMine = () => {
            return networkEntitiesService.isMine(this.modelUID);
        };
        this.hasNetworkOwner = () => {
            const entity = networkEntitiesService.getNetworkEntity(this.modelUID);
            if (entity === undefined)
                return false;
            if (entity.facet.ownerId === undefined)
                return false;
            return coreUserService.getUserByClientIdx(entity.facet.ownerId) !== undefined;
        };
        this.controlledByOthers = () => {
            return networkEntitiesService.controlledByOthers(this.modelUID);
        };
        this.getNetworkOwner = () => {
            const entity = networkEntitiesService.getNetworkEntity(this.modelUID);
            if (entity === undefined)
                return undefined;
            if (entity.facet.ownerId === undefined)
                return undefined;
            const user = coreUserService.getUserByClientIdx(entity.facet.ownerId);
            if (user === undefined)
                return undefined;
            return user.clientId;
        };
        this.getNetworkCreator = () => {
            const entity = networkEntitiesService.getNetworkEntity(this.modelUID);
            if (entity === undefined)
                return undefined;
            if (entity.facet.creatorId === undefined)
                return undefined;
            const user = coreUserService.getUserByClientIdx(entity.facet.creatorId);
            if (user === undefined)
                return undefined;
            return user.clientId;
        };
        /// Pickup model ///
        this.canPickup = (controllerUid) => __awaiter(this, void 0, void 0, function* () {
            if (this.context === undefined || this.modelUID === undefined)
                return false;
            try {
                yield this.context.connection.askByMessageType(new RequestCanPickupModelMessage(this.modelUID, controllerUid), AskOkMessage);
            }
            catch (_a) {
                return false;
            }
            return true;
        });
        /**
         * Picks up this model. It returns the uid of the picked up model, if the pickup process was successful and otherwise
         * undefined. An uid is returned since the picked up model can be a clone of this model. This is done if
         * `createTemporaryModel` is set to true.
         */
        this.pickup = (...args_1) => __awaiter(this, [...args_1], void 0, function* (params = {}) {
            let pickupOffset;
            if (params.offset !== undefined) {
                pickupOffset = {
                    position: convertToNumber3(params.offset.position),
                    orientation: convertToNumber4(params.offset.orientation),
                    scale: convertToNumber3(params.offset.scale)
                };
            }
            let modelUID = this.modelUID;
            try {
                const response = yield this.context.connection.askByMessageType(new PickupModelMessage(this.modelUID, {
                    controllerUid: params.controllerUid,
                    offset: pickupOffset,
                    allowQuickReplacement: params.allowQuickReplacement,
                    dropOffMode: params.dropOffMode,
                    dropOffLayers: params.dropOffLayers,
                    dropOffButton: params.dropOffButton,
                    createTemporaryModel: params.createTemporaryModel,
                    deleteOnDropOff: params.deleteOnDropOff,
                }), PickupModelResponseMessage);
                modelUID = response.modelUID;
            }
            catch (error) {
                console.warn("[scripting]", error);
                return undefined;
            }
            return modelUID;
        });
        this.drop = (modelUID) => {
            const uid = modelUID !== null && modelUID !== void 0 ? modelUID : this.modelUID;
            this.context.pendingMessages.push(new DropModelMessage(uid));
            return true;
        };
        /// Tooltip ///
        this.createToolTipModel = (position, normal, scale, capturedText, color) => __awaiter(this, void 0, void 0, function* () {
            const obj = new Object3D();
            obj.quaternion.setFromUnitVectors(new Vector3(0, 0, 1), new Vector3().copy(normal).normalize());
            const answer = yield this.context.connection.askByMessageType(new AddAnnotationIntoCurrentRoomRequestMessage(convertToNumber3(position), convertToNumber4(obj.quaternion), convertToNumber3(scale), capturedText, color.getHex()), AddAnnotationIntoCurrentRoomResponseMessage);
            return answer.uid;
        });
        /// Drop file ///
        this.subscribeDropFile = (callback) => {
            dropFileService.subscribeByUID(this.modelUID, callback);
        };
        this.unsubscribeDropFile = () => {
            dropFileService.unsubscribeByUID(this.modelUID);
        };
        /// RPC ///
        this.rpc = (funcName, ...params) => {
            if (typeof funcName !== "string") {
                throw new Error("RPC: funcName must be of type string");
            }
            if (SCRIPT_FUNCTION_NAMES_SET.has(funcName)) {
                throw new Error(`RPC: this funcName ${funcName} isn't allowed`);
            }
            for (const param of params) {
                const type = typeof param;
                if (!(type === "boolean" || type === "number" || type === "string" || type === "undefined")) {
                    throw new Error(`RPC: parameter ${String(param)} with type ${type} isn't supported`);
                }
            }
            this.context.connection.send(new ScriptRPCMessage(this.modelUID, hashCode(this.scriptId), hashCode(funcName), params));
        };
        this.rpcAndLocal = (funcName, ...params) => {
            this.rpc(funcName, ...params);
            const script = this.context.scripts.get(this.scriptIdx);
            if (script === undefined)
                throw new Error(`rpcAndLocal: local script not found`);
            const instance = script.scriptInstance;
            if (instance === undefined) {
                throw new Error(`rpcAndLocal: script is not loaded`);
            }
            const func = instance[funcName];
            if (typeof func !== "function") {
                throw new Error(`rpcAndLocal: property ${funcName} is not a function`);
            }
            func.call(instance, ...params, this.modelUID);
        };
        this.rpcOnlyHost = (funcName, ...params) => {
            if (typeof funcName !== "string") {
                throw new Error("RPC: funcName must be of type string");
            }
            if (SCRIPT_FUNCTION_NAMES_SET.has(funcName)) {
                throw new Error(`RPC: this funcName ${funcName} isn't allowed`);
            }
            for (const param of params) {
                const type = typeof param;
                if (!(type === "boolean" || type === "number" || type === "string" || type === "undefined")) {
                    throw new Error(`RPC: parameter ${String(param)} with type ${type} isn't supported`);
                }
            }
            this.context.connection.send(new ScriptRPCOnlyHostMessage(this.modelUID, hashCode(this.scriptId), hashCode(funcName), params));
        };
        this.rpcOnlyHostAndLocal = (funcName, ...params) => {
            this.rpcOnlyHost(funcName, ...params);
            const script = this.context.scripts.get(this.scriptIdx);
            if (script === undefined)
                throw new Error(`rpcOnlyHostAndLocal: local script not found`);
            const instance = script.scriptInstance;
            if (instance === undefined) {
                throw new Error(`rpcOnlyHostAndLocal: script is not loaded`);
            }
            const func = instance[funcName];
            if (typeof func !== "function") {
                throw new Error(`rpcOnlyHostAndLocal: property ${funcName} is not a function`);
            }
            func.call(instance, ...params, this.modelUID);
        };
        // Specify broadcast properties
        this.broadcastProps = {
            broadcast: true,
            server: false,
            peer: false,
            sendImmediately: true,
            externalMessages: this.context.pendingMessages // ToDo: this might make issues, better use BusMessage[] in favor of PendingMessages
        };
        model.persistentVariable = this.persistentVariable;
        /// Script methods ///
        // General
        model.getRelativeId = this.getRelativeId;
        model.getName = this.getName;
        model.destroyMe = this.destroyMe;
        // Find script
        model.getScriptByClassName = this.getScriptByClassName;
        model.findScriptsByClassInChildren = this.findScriptsByClassInChildren;
        // Other script functions
        model.activateModelScripts = this.activateModelScripts;
        // Emit function
        model.emit = this.emit;
        model.emitParent = this.emitParent;
        model.emitAll = this.emitAll;
        model.emitTo = this.emitTo;
        /// Common model methods ///
        // Find model
        model.getModel = this.getModel;
        model.getModelByUid = this.getModelByUid;
        model.getParentModel = this.getParentModel;
        model.getChildModelByRelativeId = this.getChildModelByRelativeId;
        model.findModelsByName = this.findModelsByName;
        model.findModelsByClass = this.findModelsByClass;
        model.findModelsByFacetType = this.findModelsByFacetType;
        // Facet
        model.updateFacet = this.updateFacet;
        model.removeFacet = this.removeFacet;
        model.getFacet = this.getFacet;
        model.hasFacet = this.hasFacet;
        model.setFacetVisibility = this.setFacetVisibility;
        // UI3D
        model.getUiElement = this.getUiElement;
        // Material
        model.setMaterialColor = this.setMaterialColor;
        model.setMaterialParameter = this.setMaterialParameter;
        model.getMaterialParameter = this.getMaterialParameter;
        // Transform
        model.setPosition = this.setPosition;
        model.setOrientation = this.setOrientation;
        model.setScale = this.setScale;
        model.setTransform = this.setTransform;
        model.setWorldPosition = this.setWorldPosition;
        model.setWorldOrientation = this.setWorldOrientation;
        model.setWorldScale = this.setWorldScale;
        model.getMatrix = this.getMatrix;
        model.getPosition = this.getPosition;
        model.getOrientation = this.getOrientation;
        model.getScale = this.getScale;
        model.getWorldMatrix = this.getWorldMatrix;
        model.getWorldPosition = this.getWorldPosition;
        model.getWorldOrientation = this.getWorldOrientation;
        model.getWorldScale = this.getWorldScale;
        /// Additional methods ///
        // Messaging
        model.subscribeBusMessage = this.subscribeBusMessage;
        model.unsubscribeBusMessage = this.unsubscribeBusMessage;
        model.unsubscribeAllBusMessage = this.unsubscribeAllBusMessage;
        model.sendBusMessage = this.sendBusMessage;
        model.controlAnimation = this.controlAnimation;
        // User input
        model.enableGesture = this.enableGesture;
        model.enableControllerAnnotation = this.enableControllerAnnotation;
        model.setAllButtonsToOneButtonType = this.setAllButtonsToOneButtonType;
        model.setControllerStrategyForHands = this.setControllerStrategyForHands;
        // Audio
        model.playSound = this.playSound;
        model.broadcastSound = this.broadcastSound;
        // Network
        model.localOnly = this.localOnly;
        model.setNetworked = this.setNetworked;
        model.takeOwnership = this.takeOwnership;
        model.surrenderOwnership = this.surrenderOwnership;
        model.isHost = this.isHost;
        model.isMine = this.isMine;
        model.controlledByOthers = this.controlledByOthers;
        model.hasNetworkOwner = this.hasNetworkOwner;
        model.getNetworkOwner = this.getNetworkOwner;
        model.getNetworkCreator = this.getNetworkCreator;
        // Pickup model
        model.canPickup = this.canPickup;
        model.pickup = this.pickup;
        model.drop = this.drop;
        // Tooltip
        model.createToolTipModel = this.createToolTipModel;
        // Drop file
        model.subscribeDropFile = this.subscribeDropFile;
        model.unsubscribeDropFile = this.unsubscribeDropFile;
        // RPC
        model.rpc = this.rpc;
        model.rpcAndLocal = this.rpcAndLocal;
        model.rpcOnlyHost = this.rpcOnlyHost;
        model.rpcOnlyHostAndLocal = this.rpcOnlyHostAndLocal;
    }
    /// Other script functions ///
    activateModelScripts(modelUID, scriptFileIds) {
        var _a, _b;
        const model = modelPool.getModel(modelUID);
        if (model === undefined)
            return;
        const scriptFacet = (_a = model.getFacet(ScriptFacet)) === null || _a === void 0 ? void 0 : _a.clone();
        if (scriptFacet !== undefined && scriptFileIds.includes(scriptFacet.scriptId) && !scriptFacet.active) {
            scriptFacet.active = true;
            modelPool.addMessage(new UpdateFacetOnModelMessage(modelUID, scriptFacet), this.broadcastProps);
        }
        const scriptComposeFacet = (_b = model.getFacet(ScriptComposeFacet)) === null || _b === void 0 ? void 0 : _b.clone();
        if (scriptComposeFacet === undefined) {
            return;
        }
        let wasChanged = false;
        for (const script of scriptComposeFacet.scripts) {
            if (!scriptFileIds.includes(script.scriptId) || script.active)
                continue;
            script.active = true;
            wasChanged = true;
        }
        if (wasChanged) {
            modelPool.addMessage(new UpdateFacetOnModelMessage(modelUID, scriptComposeFacet), this.broadcastProps);
        }
    }
    // =========================================================================
    // Additional methods
    // =========================================================================
    /// Messaging ///
    subscribeBusMessage(topic, callback) {
        const script = this.context.scripts.get(this.scriptIdx);
        if (script === undefined)
            throw new Error("Can't find script");
        return script.subscribeBusMessage(topic, callback);
    }
    unsubscribeBusMessage(topic) {
        const script = this.context.scripts.get(this.scriptIdx);
        if (script === undefined)
            throw new Error("Can't find script");
        return script.unsubscribeBusMessage(topic);
    }
    unsubscribeAllBusMessage() {
        const script = this.context.scripts.get(this.scriptIdx);
        if (script === undefined)
            throw new Error("Can't find script");
        script.unsubscribeAllBusMessage();
    }
    sendBusMessage(topic, data) {
        scriptBusService.sendBusMessage(this.room, this.scriptIdx, topic, data);
    }
    /// Private helper methods ///
    getParentRoom(uid) {
        var _a, _b;
        return (_b = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getRoom()) !== null && _b !== void 0 ? _b : undefined;
    }
}
